import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { 
  CreditCard, 
  DollarSign, 
  Smartphone, 
  Banknote, 
  Building2, 
  Send,
  Trash2,
  Check,
  AlertCircle,
  Plus
} from 'lucide-react';
import { toast } from 'sonner';
import { paymentAPI } from '@/services/api';

const METODOS_PAGO = [
  { id: 'pago_movil', nombre: 'Pago Móvil', icon: Smartphone, color: 'bg-green-500' },
  { id: 'efectivo_usd', nombre: 'Efectivo (USD)', icon: DollarSign, color: 'bg-emerald-500' },
  { id: 'efectivo_bs', nombre: 'Efectivo (Bs)', icon: Banknote, color: 'bg-yellow-500' },
  { id: 'tarjeta_debito', nombre: 'Tarjeta Débito', icon: CreditCard, color: 'bg-blue-500' },
  { id: 'tarjeta_credito', nombre: 'Tarjeta Crédito', icon: CreditCard, color: 'bg-purple-500' },
  { id: 'transferencia', nombre: 'Transferencia', icon: Building2, color: 'bg-cyan-500' },
  { id: 'paypal', nombre: 'PayPal', icon: Send, color: 'bg-blue-600' },
  { id: 'zelle', nombre: 'Zelle', icon: Send, color: 'bg-indigo-500' },
];

export default function PaymentModal({ open, onClose, reservation, onPaymentComplete }) {
  const [loading, setLoading] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [formData, setFormData] = useState({
    monto: '',
    metodo_pago: '',
    referencia: '',
    notas: ''
  });

  useEffect(() => {
    if (open && reservation) {
      fetchPayments();
    }
  }, [open, reservation]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const response = await paymentAPI.getByReservation(reservation.id);
      setPaymentInfo(response.data);
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast.error('Error al cargar pagos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitPayment = async (e) => {
    e.preventDefault();
    
    if (!formData.monto || !formData.metodo_pago) {
      toast.error('Completa los campos requeridos');
      return;
    }

    const monto = parseFloat(formData.monto);
    if (isNaN(monto) || monto <= 0) {
      toast.error('Ingresa un monto válido');
      return;
    }

    try {
      setLoading(true);
      await paymentAPI.create({
        reservation_id: reservation.id,
        monto: monto,
        metodo_pago: formData.metodo_pago,
        referencia: formData.referencia || null,
        notas: formData.notas || null
      });
      
      toast.success('Pago registrado exitosamente');
      setFormData({ monto: '', metodo_pago: '', referencia: '', notas: '' });
      setShowAddPayment(false);
      fetchPayments();
      
      if (onPaymentComplete) {
        onPaymentComplete();
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al registrar pago');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('¿Estás seguro de eliminar este pago?')) return;
    
    try {
      await paymentAPI.delete(paymentId);
      toast.success('Pago eliminado');
      fetchPayments();
      if (onPaymentComplete) onPaymentComplete();
    } catch (error) {
      toast.error('Error al eliminar pago');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-VE', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const getMethodInfo = (methodId) => {
    return METODOS_PAGO.find(m => m.id === methodId) || { nombre: methodId, icon: DollarSign, color: 'bg-gray-500' };
  };

  if (!reservation) return null;

  const precioTotal = reservation.precio_total || 0;
  const totalPagado = paymentInfo?.total_pagado || 0;
  const saldoPendiente = paymentInfo?.saldo_pendiente || precioTotal;
  const pagos = paymentInfo?.pagos || [];
  const porcentajePagado = precioTotal > 0 ? (totalPagado / precioTotal) * 100 : 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-primary" />
            Pagos de Reserva
          </DialogTitle>
          <DialogDescription>
            {reservation.guest_name} {reservation.guest_lastname} - {reservation.fuente}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Summary Card */}
          <div className="bg-gradient-to-r from-primary/10 to-blue-500/10 p-4 rounded-lg border">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-xs text-muted-foreground">Total Reserva</p>
                <p className="text-lg font-bold">{formatCurrency(precioTotal)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Pagado</p>
                <p className="text-lg font-bold text-green-600">{formatCurrency(totalPagado)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Pendiente</p>
                <p className={`text-lg font-bold ${saldoPendiente > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {formatCurrency(saldoPendiente)}
                </p>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="mt-4">
              <div className="flex justify-between text-xs mb-1">
                <span>Progreso de pago</span>
                <span>{porcentajePagado.toFixed(0)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all ${porcentajePagado >= 100 ? 'bg-green-500' : 'bg-primary'}`}
                  style={{ width: `${Math.min(porcentajePagado, 100)}%` }}
                />
              </div>
            </div>

            {saldoPendiente <= 0 && (
              <div className="mt-3 flex items-center gap-2 text-green-600 bg-green-50 p-2 rounded">
                <Check className="h-4 w-4" />
                <span className="text-sm font-medium">Reserva pagada completamente</span>
              </div>
            )}
          </div>

          {/* Payment History */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">Historial de Pagos</h3>
              {saldoPendiente > 0 && (
                <Button 
                  size="sm" 
                  onClick={() => setShowAddPayment(!showAddPayment)}
                  variant={showAddPayment ? "outline" : "default"}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  {showAddPayment ? 'Cancelar' : 'Agregar Pago'}
                </Button>
              )}
            </div>

            {pagos.length === 0 ? (
              <div className="text-center py-6 bg-muted/30 rounded-lg">
                <AlertCircle className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />
                <p className="text-muted-foreground text-sm">No hay pagos registrados</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {pagos.map((pago) => {
                  const method = getMethodInfo(pago.metodo_pago);
                  const Icon = method.icon;
                  return (
                    <div 
                      key={pago.id} 
                      className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 ${method.color} rounded-full flex items-center justify-center`}>
                          <Icon className="h-4 w-4 text-white" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{method.nombre}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(pago.created_at).toLocaleDateString('es-VE', {
                              day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                            })}
                          </p>
                          {pago.referencia && (
                            <p className="text-xs text-muted-foreground">Ref: {pago.referencia}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-green-600">{formatCurrency(pago.monto)}</span>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-6 w-6 text-red-500 hover:text-red-700"
                          onClick={() => handleDeletePayment(pago.id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Add Payment Form */}
          {showAddPayment && saldoPendiente > 0 && (
            <form onSubmit={handleSubmitPayment} className="space-y-4 border-t pt-4">
              <h3 className="font-semibold">Registrar Nuevo Pago</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Monto (USD) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={saldoPendiente}
                    placeholder={`Máx: ${saldoPendiente.toFixed(2)}`}
                    value={formData.monto}
                    onChange={(e) => setFormData({...formData, monto: e.target.value})}
                    required
                  />
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm" 
                    className="w-full text-xs"
                    onClick={() => setFormData({...formData, monto: saldoPendiente.toFixed(2)})}
                  >
                    Pagar total pendiente ({formatCurrency(saldoPendiente)})
                  </Button>
                </div>
                
                <div className="space-y-2">
                  <Label>Método de Pago *</Label>
                  <Select 
                    value={formData.metodo_pago} 
                    onValueChange={(v) => setFormData({...formData, metodo_pago: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar..." />
                    </SelectTrigger>
                    <SelectContent>
                      {METODOS_PAGO.map((method) => {
                        const Icon = method.icon;
                        return (
                          <SelectItem key={method.id} value={method.id}>
                            <div className="flex items-center gap-2">
                              <Icon className="h-4 w-4" />
                              {method.nombre}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Referencia de Transacción (opcional)</Label>
                <Input
                  placeholder="Ej: Confirmación #12345"
                  value={formData.referencia}
                  onChange={(e) => setFormData({...formData, referencia: e.target.value})}
                />
              </div>

              <div className="space-y-2">
                <Label>Notas (opcional)</Label>
                <Textarea
                  placeholder="Observaciones adicionales..."
                  rows={2}
                  value={formData.notas}
                  onChange={(e) => setFormData({...formData, notas: e.target.value})}
                />
              </div>

              <div className="flex gap-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setShowAddPayment(false)}
                >
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1" disabled={loading}>
                  {loading ? 'Guardando...' : 'Registrar Pago'}
                </Button>
              </div>
            </form>
          )}

          {/* Close Button */}
          <div className="flex justify-end pt-2 border-t">
            <Button variant="outline" onClick={onClose}>
              Cerrar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
